/**
 * `Gallery` (W1-12 ui-kit-i18n; ARCHITECTURE.md §3.2): the dev-only route `#/dev/ui` (registered by the shell)
 * showing every primitive of src/ui in its states, for visual QA in the three themes, both languages, with
 * reduced motion, and for keyboard checks. It runs on whatever UiBridge is mounted (the inert bridge outside the
 * app: chord labels are the raw chord text and commands do nothing).
 */

import { useState, type ReactNode } from 'react';
import type { RGB } from '../core/util/types';
import { Beats } from '../core/time/beat';
import { createTempoMap } from '../core/time/tempo';
import type { Beat, BeatParseContext } from '../core/time/types';
import { i18n, LOCALE_NAMES, LOCALES } from '../i18n/runtime';
import { useLocale, useT } from '../i18n/useT';
import type { Locale } from '../core/util/types';
import { Badge } from './Badge';
import { BeatField } from './BeatField';
import { Button } from './Button';
import { Checkbox } from './Checkbox';
import { ColorField } from './ColorField';
import { ContextMenu } from './ContextMenu';
import { CurveThumb } from './CurveThumb';
import { Dialog } from './Dialog';
import { EmptyState } from './EmptyState';
import { Field } from './Field';
import s from './Gallery.module.css';
import { IconButton } from './IconButton';
import { useReducedMotion } from './internal/useReducedMotion';
import { Kbd } from './Kbd';
import { Menu } from './Menu';
import { NumberField } from './NumberField';
import { Popover } from './Popover';
import { applyUiPreferences, type UiTheme } from './preferences';
import { ProgressBar } from './ProgressBar';
import { Section } from './Section';
import { Segmented } from './Segmented';
import { Select } from './Select';
import { Sheet } from './Sheet';
import { Slider } from './Slider';
import { Splitter } from './Splitter';
import { Swatch } from './Swatch';
import { Switch } from './Switch';
import { Tabs } from './Tabs';
import { TextArea } from './TextArea';
import { TextField } from './TextField';
import { Toolbar, ToolbarSeparator } from './Toolbar';
import { Tooltip } from './Tooltip';
import type { FieldValue, MenuItem } from './types';
import { m } from './ui.messages';
import { VirtualList } from './VirtualList';

const TEMPO = createTempoMap([
  { bpm: 120, startTime: [0, 0, 1] },
  { bpm: 172, startTime: [32, 0, 1] },
]);
const BEAT_CTX: BeatParseContext = { snapDiv: 4, syntax: 'default', tempo: TEMPO };
const LINES = Array.from({ length: 2000 }, (_, i) => i);

type NoteKind = 'tap' | 'drag' | 'flick' | 'hold';
type Side = 'top' | 'bottom';
type TabId = 'lines' | 'problems' | 'history';

function GallerySection(props: { readonly title: string; readonly children: ReactNode }) {
  return (
    <section className={s.block}>
      <h2 className={s.blockTitle}>{props.title}</h2>
      <div className={s.blockBody}>{props.children}</div>
    </section>
  );
}

export function Gallery() {
  const t = useT();
  const locale = useLocale();
  const reduced = useReducedMotion();
  const [theme, setTheme] = useState<UiTheme>('dark');
  const [speed, setSpeed] = useState<FieldValue<number>>({ kind: 'value', value: 1 });
  const [rotation, setRotation] = useState<FieldValue<number>>({ kind: 'value', value: 45 });
  const [opacityRaw, setOpacityRaw] = useState(false);
  const [opacity, setOpacity] = useState(255);
  const [time, setTime] = useState<Beat>(Beats.make(12, 3, 4));
  const [noteColour, setNoteColour] = useState<RGB | null>(null);
  const [tagColour, setTagColour] = useState<RGB>([10, 195, 255]);
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [json, setJson] = useState('{\n  "isCover": 1\n}');
  const [kind, setKind] = useState<NoteKind>('tap');
  const [side, setSide] = useState<Side | 'mixed'>('mixed');
  const [fake, setFake] = useState<boolean | 'mixed'>(false);
  const [cover, setCover] = useState(true);
  const [metronome, setMetronome] = useState(false);
  const [volume, setVolume] = useState(80);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [tab, setTab] = useState<TabId>('lines');
  const [sizes, setSizes] = useState<readonly number[]>([35, 65]);
  const [selectedLine, setSelectedLine] = useState<number>(0);
  const [pressed, setPressed] = useState(true);
  const [progress, setProgress] = useState(0.42);

  const changeTheme = (next: UiTheme): void => {
    setTheme(next);
    applyUiPreferences({ theme: next });
  };

  const menuItems: readonly MenuItem[] = [
    { kind: 'action', label: t(m['gallery.copy']), icon: 'Copy', onSelect: () => undefined },
    {
      kind: 'action',
      label: t(m['gallery.paste']),
      icon: 'ClipboardPaste',
      disabledReason: { key: m['gallery.disabledReason'] },
      onSelect: () => undefined,
    },
    { kind: 'separator' },
    {
      kind: 'submenu',
      label: t(m['gallery.more']),
      items: [
        { kind: 'action', label: t(m['gallery.mirror']), icon: 'ArrowLeftRight', onSelect: () => undefined },
        { kind: 'action', label: t(m['gallery.reverse']), onSelect: () => undefined },
      ],
    },
    { kind: 'command', command: 'edit.undo', label: t(m['gallery.undo']) },
  ];

  return (
    <div className={s.gallery}>
      <header className={s.header}>
        <div>
          <h1 className={s.title}>{t(m['gallery.title'])}</h1>
          <p className={s.intro}>{t(m['gallery.intro'])}</p>
        </div>
        <div className={s.controls}>
          <Segmented<UiTheme>
            label={t(m['gallery.theme'])}
            value={theme}
            onChange={changeTheme}
            options={[
              { value: 'dark', label: t(m['gallery.theme.dark']), icon: 'Moon' },
              { value: 'light', label: t(m['gallery.theme.light']), icon: 'Sun' },
              { value: 'highContrast', label: t(m['gallery.theme.highContrast']), icon: 'Contrast' },
            ]}
          />
          <Select<Locale>
            label={t(m['gallery.language'])}
            value={locale}
            onChange={(l) => {
              i18n.setLocale(l);
            }}
            options={LOCALES.map((l) => ({ value: l, label: LOCALE_NAMES[l], icon: 'Languages' }))}
          />
          <Switch
            label={t(m['gallery.reducedMotion'])}
            checked={reduced}
            onChange={(on) => {
              applyUiPreferences({ reducedMotion: on ? 'on' : 'off' });
            }}
          />
        </div>
      </header>

      <div className={s.grid}>
        <GallerySection title={t(m['gallery.buttons'])}>
          <div className={s.row}>
            <Button variant="primary" icon="FileDown">
              {t(m['gallery.primary'])}
            </Button>
            <Button icon="Play">{t(m['gallery.secondary'])}</Button>
            <Button variant="ghost">{t(m['gallery.ghost'])}</Button>
            <Button variant="danger" icon="Trash2">
              {t(m['gallery.danger'])}
            </Button>
            <Button disabledReason={{ key: m['gallery.disabledReason'] }}>{t(m['gallery.disabled'])}</Button>
          </div>
          <div className={s.row}>
            <IconButton icon="Play" label={{ key: m['gallery.play'] }} command="playback.toggle" />
            <IconButton
              icon="Magnet"
              label={{ key: m['gallery.snap'] }}
              pressed={pressed}
              onClick={() => {
                setPressed(!pressed);
              }}
            />
            <IconButton icon="Undo2" label={{ key: m['gallery.undo'] }} command="edit.undo" />
            <IconButton
              icon="Trash2"
              label={{ key: m['gallery.danger'] }}
              disabledReason={{ key: m['gallery.disabledReason'] }}
            />
            <IconButton icon="Settings2" label={{ key: m['gallery.more'] }} size="sm" />
            <Kbd chord="Ctrl+KeyZ" />
            <Kbd chord="Alt+Comma" />
          </div>
        </GallerySection>

        <GallerySection title={t(m['gallery.badges'])}>
          <div className={s.row}>
            <Badge kind="inUse" />
            <Badge kind="phiraIgnores" />
            <Badge kind="rpe17" />
            <Badge kind="editorOnly" />
            <Badge kind="baked" />
            <Badge kind="full" />
          </div>
          <div className={s.row}>
            <Badge kind="problem" severity="fatal" onClick={() => undefined} />
            <Badge kind="problem" severity="error" onClick={() => undefined} />
            <Badge kind="problem" severity="warning" onClick={() => undefined} />
            <Badge kind="problem" severity="tip" onClick={() => undefined} />
          </div>
        </GallerySection>

        <GallerySection title={t(m['gallery.fields'])}>
          <NumberField
            label={t(m['gallery.speed'])}
            value={speed}
            step={0.1}
            precision={2}
            unit={t(m['gallery.unitTimes'])}
            help={{ key: m['gallery.speedHelp'] }}
            onScrub={(v) => {
              setSpeed({ kind: 'value', value: v });
            }}
            onCommit={(c) => {
              if (c.kind === 'number') setSpeed({ kind: 'value', value: c.value });
            }}
          />
          <NumberField
            label={t(m['gallery.positionX'])}
            value={{ kind: 'mixed' }}
            unit={t(m['gallery.unitPx'])}
            vars="noteField"
            badges={[{ kind: 'inUse' }]}
            onCommit={() => undefined}
          />
          <NumberField
            label={t(m['gallery.rotation'])}
            value={rotation}
            unit={t(m['gallery.unitDeg'])}
            badges={[{ kind: 'problem', severity: 'warning' }]}
            onCommit={(c) => {
              if (c.kind === 'number') setRotation({ kind: 'value', value: c.value });
            }}
          />
          <NumberField
            label={t(m['gallery.opacity'])}
            value={{ kind: 'value', value: opacityRaw ? opacity : (opacity / 255) * 100 }}
            min={0}
            max={opacityRaw ? 255 : 100}
            precision={opacityRaw ? 0 : 1}
            unitToggle={{
              raw: t(m['gallery.unitRaw']),
              friendly: t(m['gallery.unitPercent']),
              mode: opacityRaw ? 'raw' : 'friendly',
              onToggle: () => {
                setOpacityRaw(!opacityRaw);
              },
            }}
            onCommit={(c) => {
              if (c.kind === 'number') setOpacity(Math.round(opacityRaw ? c.value : (c.value / 100) * 255));
            }}
          />
          <BeatField
            label={t(m['gallery.time'])}
            value={{ kind: 'value', value: time }}
            ctx={BEAT_CTX}
            echo
            onCommit={(b) => {
              setTime(b);
            }}
          />
          <ColorField
            label={t(m['gallery.noteColour'])}
            value={noteColour === null ? null : { kind: 'value', value: noteColour }}
            allowNone
            onCommit={setNoteColour}
          />
          <ColorField
            label={t(m['gallery.lineColour'])}
            value={{ kind: 'value', value: tagColour }}
            onCommit={(c) => {
              if (c !== null) setTagColour(c);
            }}
          />
          <TextField
            label={t(m['gallery.name'])}
            value={name}
            placeholder={{ key: m['gallery.namePlaceholder'] }}
            onCommit={setName}
          />
          <TextArea label={t(m['gallery.notes'])} value={notes} rows={2} onCommit={setNotes} />
          <TextArea label={t(m['gallery.json'])} value={json} rows={3} code onCommit={setJson} />
        </GallerySection>

        <GallerySection title={t(m['gallery.choices'])}>
          <Field label={t(m['gallery.noteType'])} help={{ key: m['gallery.speedHelp'] }}>
            <Segmented<NoteKind>
              value={kind}
              onChange={setKind}
              ariaLabel={t(m['gallery.noteType'])}
              options={[
                { value: 'tap', label: t(m['gallery.tap']) },
                { value: 'drag', label: t(m['gallery.drag']) },
                { value: 'flick', label: t(m['gallery.flick']) },
                { value: 'hold', label: t(m['gallery.hold']), disabledReason: { key: m['gallery.disabledReason'] } },
              ]}
            />
          </Field>
          <Field label={t(m['gallery.side'])} badges={[{ kind: 'phiraIgnores' }]}>
            <Select<Side>
              value={side}
              onChange={setSide}
              ariaLabel={t(m['gallery.side'])}
              options={[
                { value: 'top', label: t(m['gallery.sideTop']), icon: 'ArrowUpToLine' },
                { value: 'bottom', label: t(m['gallery.sideBottom']), icon: 'ArrowDownToLine' },
              ]}
            />
          </Field>
          <Checkbox
            label={t(m['gallery.fake'])}
            checked={fake}
            onChange={(v) => {
              setFake(v);
            }}
          />
          <Checkbox label={t(m['gallery.mixedCheckbox'])} checked="mixed" onChange={() => undefined} />
          <Field
            label={t(m['gallery.cover'])}
            info={{
              text: { key: m['gallery.coverInfo'] },
              rpeTerm: t(m['gallery.coverRpeTerm']),
              communityTerm: t(m['gallery.coverCommunityTerm']),
              illustration: 'cover',
            }}
          >
            <Switch label={t(m['gallery.cover'])} checked={cover} onChange={setCover} />
          </Field>
          <Switch
            label={t(m['gallery.metronome'])}
            checked={metronome}
            onChange={setMetronome}
            disabledReason={metronome ? null : { key: m['gallery.disabledReason'] }}
          />
          <Slider
            label={t(m['gallery.volume'])}
            value={volume}
            min={0}
            max={100}
            format={(v) => t(m['progress.percent'], { value: v / 100 })}
            onChange={(v) => {
              setVolume(v);
            }}
          />
        </GallerySection>

        <GallerySection title={t(m['gallery.overlays'])}>
          <div className={s.row}>
            <Button
              onClick={() => {
                setDialogOpen(true);
              }}
            >
              {t(m['gallery.openDialog'])}
            </Button>
            <Button
              onClick={() => {
                setSheetOpen(true);
              }}
            >
              {t(m['gallery.openSheet'])}
            </Button>
            <Popover trigger={<Button icon="SlidersHorizontal">{t(m['gallery.popover'])}</Button>}>
              <p>{t(m['gallery.popoverBody'])}</p>
            </Popover>
            <Tooltip content={t(m['gallery.tooltipBody'])} command="edit.undo">
              <Button variant="ghost">{t(m['gallery.tooltip'])}</Button>
            </Tooltip>
            <Menu trigger={<Button icon="ChevronDown">{t(m['gallery.menu'])}</Button>} items={menuItems} />
          </div>
          <ContextMenu items={menuItems}>
            <div className={s.contextArea} tabIndex={0}>
              {t(m['gallery.contextMenu'])}
            </div>
          </ContextMenu>
          <Dialog
            open={dialogOpen}
            title={t(m['gallery.dialogTitle'])}
            description={t(m['gallery.dialogBody'])}
            size="sm"
            onClose={() => {
              setDialogOpen(false);
            }}
            footer={
              <>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setDialogOpen(false);
                  }}
                >
                  {t(m['gallery.ghost'])}
                </Button>
                <Button
                  variant="danger"
                  autoFocus
                  onClick={() => {
                    setDialogOpen(false);
                  }}
                >
                  {t(m['gallery.danger'])}
                </Button>
              </>
            }
          >
            <ProgressBar value={null} label={{ key: m['gallery.progress'] }} />
          </Dialog>
          <Sheet
            open={sheetOpen}
            title={t(m['gallery.sheetTitle'])}
            onClose={() => {
              setSheetOpen(false);
            }}
            footer={
              <Button
                variant="primary"
                onClick={() => {
                  setSheetOpen(false);
                }}
              >
                {t(m['gallery.apply'])}
              </Button>
            }
          >
            <p>{t(m['gallery.sheetBody'])}</p>
            <NumberField label={t(m['gallery.speed'])} value={speed} step={0.1} onCommit={() => undefined} />
          </Sheet>
        </GallerySection>

        <GallerySection title={t(m['gallery.tabs'])}>
          <Tabs<TabId>
            value={tab}
            onChange={setTab}
            label={t(m['gallery.tabs'])}
            tabs={[
              { value: 'lines', label: t(m['gallery.tabLines']), icon: 'Rows3' },
              {
                value: 'problems',
                label: t(m['gallery.tabProblems']),
                icon: 'ListChecks',
                badge: (
                  <Badge
                    kind="problem"
                    severity="error"
                    label={{ key: m['gallery.problemCount'], params: { count: 3 } }}
                  />
                ),
              },
              { value: 'history', label: t(m['gallery.tabHistory']), icon: 'History' },
            ]}
          >
            <p className={s.tabBody}>{t(m['gallery.tabContent'], { tab })}</p>
          </Tabs>
        </GallerySection>

        <GallerySection title={t(m['gallery.lists'])}>
          <Toolbar label={{ key: m['gallery.toolbar'] }}>
            <IconButton icon="MousePointer2" label={{ key: m['gallery.select'] }} pressed />
            <IconButton icon="Plus" label={{ key: m['gallery.place'] }} />
            <IconButton icon="Eraser" label={{ key: m['gallery.erase'] }} />
            <ToolbarSeparator />
            <IconButton
              icon="Undo2"
              label={{ key: m['gallery.undo'] }}
              disabledReason={{ key: m['gallery.disabledReason'] }}
            />
          </Toolbar>
          <div className={s.split}>
            <Splitter orientation="horizontal" sizes={sizes} onResize={setSizes}>
              <div className={s.pane}>
                <VirtualList<number>
                  items={LINES}
                  rowHeight={24}
                  getKey={(i) => i}
                  selected={new Set([selectedLine])}
                  ariaLabel={{ key: m['gallery.listLabel'] }}
                  onActivate={(i) => {
                    setSelectedLine(i);
                  }}
                  render={(i) => t(m['gallery.lineName'], { index: i })}
                />
              </div>
              <div className={s.pane}>
                <Section
                  title={t(m['gallery.section'])}
                  collapsible
                  actions={<IconButton icon="Settings2" label={{ key: m['gallery.more'] }} size="sm" />}
                >
                  <p>{t(m['gallery.sectionBody'])}</p>
                </Section>
                <EmptyState
                  icon="ListChecks"
                  title={{ key: m['gallery.empty'] }}
                  body={{ key: m['gallery.emptyBody'] }}
                  actions={[{ label: { key: m['gallery.emptyAction'] }, command: 'problems.run' }]}
                />
              </div>
            </Splitter>
          </div>
          <Slider
            label={t(m['gallery.progress'])}
            value={Math.round(progress * 100)}
            min={0}
            max={100}
            onChange={(v) => {
              setProgress(v / 100);
            }}
          />
          <ProgressBar value={progress} label={{ key: m['gallery.progress'] }} />
        </GallerySection>

        <GallerySection title={t(m['gallery.curves'])}>
          <div className={s.row}>
            <CurveThumb ease={{ kind: 'tween', id: 1, left: 0, right: 1 }} />
            <CurveThumb ease={{ kind: 'hold' }} />
            <CurveThumb ease={{ kind: 'tween', id: 4, left: 0, right: 1 }} />
            <CurveThumb ease={{ kind: 'tween', id: 13, left: 0, right: 1 }} />
            <CurveThumb ease={{ kind: 'tween', id: 23, left: 0, right: 1 }} size={48} showOvershoot />
            <CurveThumb ease={{ kind: 'tween', id: 29, left: 0, right: 1 }} size={48} showOvershoot />
            <CurveThumb ease={{ kind: 'tween', id: 26, left: 0, right: 1 }} size={48} />
            <CurveThumb ease={{ kind: 'tween', id: 6, left: 0.2, right: 0.8 }} size={48} />
            <CurveThumb ease={{ kind: 'bezier', p: [0.2, 1.4, 0.6, -0.3] }} size={48} showOvershoot />
          </div>
        </GallerySection>

        <GallerySection title={t(m['gallery.swatches'])}>
          <div className={s.row}>
            <Swatch color={[255, 255, 255]} />
            <Swatch color={[10, 195, 255]} size={20} />
            <Swatch color={[240, 237, 105]} size={24} />
            <Swatch color="var(--color-note-flick)" label={{ key: m['gallery.flick'] }} size={24} />
          </div>
        </GallerySection>
      </div>
    </div>
  );
}
